import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import { MathJax } from "better-react-mathjax"; // ADD THIS LINE

const TEST_DURATION = 10 * 60; 

function BehaviouralTest({ student, onComplete }) { 
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [timer, setTimer] = useState(TEST_DURATION);
  const [finished, setFinished] = useState(false);
  const [score, setScore] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${("0" + remainingSeconds).slice(-2)}`;
  };

  const calculateScoreAndSubmit = async () => {
    if (finished || submitting) return;
    setSubmitting(true);
    let finalScore = 0;
    const submissionAnswers = {};
    const topicScores = {};
    
    let attemptedQuestions = 0; 
    let totalQuestions = questions.length;

    questions.forEach((q) => {
      const selectedOptionKey = answers[q.id]; 
      // NOTE: Ensure your Behavioural view provides 'correct_answer'
      const correctLetter = q.correct_answer ? q.correct_answer.trim().toLowerCase() : null;

      if (selectedOptionKey) {
        attemptedQuestions += 1;
      }

      if (!topicScores[q.topic]) {
          topicScores[q.topic] = { score: 0, total: 0 };
      }
      topicScores[q.topic].total += 1; 

      if (selectedOptionKey) {
        const userSelectedLetter = selectedOptionKey.split('_')[1];
        if (userSelectedLetter === correctLetter) {
          finalScore += 1;
          topicScores[q.topic].score += 1;
        }
      }
      
      submissionAnswers[q.id] = selectedOptionKey && q[selectedOptionKey] 
        ? q[selectedOptionKey].trim() 
        : null; 
    });

    setScore(finalScore);
    
    const result = {
      student_name: student.name,
      student_email: student.email,
      college: student.college,
      score: finalScore,
      total_questions: totalQuestions,
      answers_submitted: submissionAnswers,
      attempted_count: attemptedQuestions
    };

    const { error } = await supabase.from("results").insert([result]);

    if (error) {
      console.error("Error submitting results (Behavioural):", error);
    }

    setSubmitting(false);
    
    onComplete('behavioural', { 
        totalScore: finalScore,
        totalQuestions: totalQuestions,
        topicBreakdown: topicScores,
        attemptedCount: attemptedQuestions, 
        unattemptedCount: totalQuestions - attemptedQuestions 
    }); 
    setFinished(true);
  };

  // --- NAVIGATION HANDLERS ---
  const handleNext = () => {
    if (current < questions.length - 1) {
      setCurrent(current + 1);
    } else {
      calculateScoreAndSubmit();
    }
  };

  const handlePrevious = () => {
    if (current > 0) {
      setCurrent(current - 1);
    }
  };
  // -----------------------------

  // Fetch Questions
  useEffect(() => {
    async function fetchQuestions() {
      setLoading(true);
      const { data, error } = await supabase
        .from("random_behavioural_questions") // NOTE: Check your view name
        .select("id, topic, question, option_a, option_b, option_c, option_d, correct_answer")
        .limit(10); 
        
      if (error) {
        console.error("Error fetching questions:", error);
        setQuestions([]);
      } else {
        setQuestions(data);
      }
      setLoading(false);
    }
    fetchQuestions();
  }, []); 

  // Timer countdown
  useEffect(() => {
    if (timer > 0 && !finished && questions.length > 0) {
      const interval = setInterval(() => setTimer((t) => t - 1), 1000);
      return () => clearInterval(interval);
    } else if (timer === 0 && !finished && questions.length > 0) {
      calculateScoreAndSubmit();
    }
  }, [timer, finished, questions.length]);

  const handleAnswer = (optionKey) => {
    const questionId = questions[current].id;
    setAnswers({ ...answers, [questionId]: optionKey });
  };
    
  // --- Render Logic ---
  if (loading || questions.length === 0) {
      return (
        <Box sx={{mt:4, textAlign: 'center'}}>
            <CircularProgress />
            <Typography>Loading Behavioural Assessment...</Typography>
        </Box>
      );
  }
  
  if (finished) { 
     return (
         <Paper elevation={6} sx={{ p: { xs: 3, md: 5 }, my: 4, textAlign: "center", borderRadius: 2 }}>
            <Typography variant="h4" gutterBottom color="primary">Assessment Complete!</Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>Returning to assessment dashboard...</Typography>
        </Paper>
    );
  }

  const question = questions[current];
  const questionOptions = [
    { key: 'option_a', text: question.option_a, label: 'A' },
    { key: 'option_b', text: question.option_b, label: 'B' },
    { key: 'option_c', text: question.option_c, label: 'C' },
    { key: 'option_d', text: question.option_d, label: 'D' },
  ];
    
  const selectedOptionKey = answers[question.id];

  return (
    <Box sx={{ mt: { xs: 0, md: 4 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1, p: 1.5, backgroundColor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 'bold' }}>
            Behavioural Question {current + 1} of {questions.length}
          </Typography>
          <Typography variant="h6" color="secondary.dark" sx={{ fontWeight: 'bold' }}>
            Time Left: {formatTime(timer)}
          </Typography>
      </Box>
        
      <Paper elevation={4} sx={{ p: { xs: 2, md: 3 }, mb: 2, borderLeft: '5px solid', borderColor: 'info.main', borderRadius: 2 }}>
        {/* FIX APPLIED HERE: Added 'display: 'none'' to hide the topic, removing invalid ** */}
        <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'none' }}>
          Topic: {question.topic}
        </Typography>
	<MathJax> {/* WRAP the question text in MathJax component */}
  		<Typography variant="h5" sx={{ fontWeight: 600, color: 'info.dark', fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
    {question.question} 
  </Typography>
</MathJax>
        
      </Paper>
        
      {/* Answer Options */}
      <Grid container spacing={1}>
        {questionOptions.map((option) => (
          <Grid item xs={12} key={option.key}>
            <Button
              variant={selectedOptionKey === option.key ? "contained" : "outlined"}
              color={selectedOptionKey === option.key ? "info" : "inherit"}
              onClick={() => handleAnswer(option.key)}
              fullWidth
              sx={{  
                justifyContent: 'flex-start',
                textAlign: 'left',
                py: 0.8,
                fontWeight: 'normal',
                fontSize: '0.9rem',
                backgroundColor: selectedOptionKey === option.key ? 'info.light' : 'white',
                '&:hover': {
                    backgroundColor: selectedOptionKey === option.key ? 'info.main' : 'background.default',
                }
              }}
            >
              <Typography variant="body1" sx={{ mr: 1.5, fontWeight: 'bold' }}>
                {option.label}.
              </Typography>
              <Typography variant="body1" component="span">
                {option.text}
              </Typography>
            </Button>
          </Grid>
        ))}
      </Grid>
        
      {/* NAVIGATION BUTTONS */}
      <Box display="flex" justifyContent="space-between" sx={{ mt: 2 }}>
        {/* Previous Button - Disabled on the first question */}
        <Button
            variant="outlined"
            color="info"
            sx={{ py: 1.2, minWidth: { xs: '30%', sm: '150px' } }}
            onClick={handlePrevious}
            disabled={current === 0 || submitting}
        >
            Previous
        </Button>
        
        {/* Next/Submit Button */}
        <Button
          variant="contained"
          color="info"
          sx={{ py: 1.2, minWidth: { xs: '65%', sm: '250px' } }}
          onClick={handleNext}
          disabled={submitting}
        >
          {current < questions.length - 1 ? "Next Question" : (submitting ? "Submitting..." : "Submit Test")}
        </Button>
      </Box>
    </Box>
  );
}

export default BehaviouralTest;